// Sentry may or may not be present in the environment where this
// runs. All functions in this file should expect it to potentially be
// missing.

// The general class name of errors generated by this module:
const AssertionFailed = 'AssertionFailed';

// Used when no context function is provided:
const EmptyContexter = () => ({});

//
// Notify Sentry if it's present on the page
//
function sentryNotify(notification) {
  const sentry = window.Sentry;
  if (sentry) {
    switch (notification.type) {
      case 'message':
        sentry.captureMessage(notification.content, {
          tags: notification.context,
        });
        break;
      case 'error':
        sentry.captureException(notification.content, {
          tags: notification.context,
        });
        break;
    }

    console.error(
      `Assertion failed; ${sentry?.getCurrentHub()?.getClient()?._isEnabled() ? 'notifying' : 'would notify'} Sentry:`,
      notification);
  } else {
    console.error('Assertion failed:', notification, 'Sentry is not available.');
  }
}

//
// Assert that some condition is true. If it is not, notify
// Sentry, and throw an error to stop execution. (HB is simply
// ignored if it's not present on the page.)
//
// The given `predicate` must be a function (taking no arguments). The
// return value of this function should be true (or truthy). If it is
// not, Sentry will be notified (if it's present on the page and
// enabled), and an error will be thrown to stop execution in the
// parent context.
//
// The `description` must be a human-readable string describing the
// condition being checked in the predicate. It must not contain any
// contextual information (such as ids).
//
// The `contexter` is optional; if given, it must be a function
// returning a JSON-compatible object that will be provided to
// Sentry to merge in with the existing context variables.
//
// Should either the predicate or contexter functions throw an error,
// that error will simply bubble up, untouched. If it reaches the top
// of the stack, it'll be reported to Sentry anyway.
//
// Returns undefined.
//
export function assert(description, predicate, contexter = EmptyContexter, notifier = sentryNotify) {
  const ok = predicate();
  if (!ok) {
    // Call into Sentry ourselves so that we can attach the context
    notifier({
      type: 'message',
      content: `${AssertionFailed}: ${description}`,
      context: contexter(),
    });

    // With that done, re-throw to stop the parent context
    const rethrown = new Error(description);
    rethrown.name = AssertionFailed;
    throw rethrown;
  }
}

//
// Safely check that some condition is true. If it is not, silently
// report it back to Sentry.
//
// This function works exactly the same as `assert`, except that it
// does not throw an error if the predicate returns a falsy value. In
// fact, even if the predicate or contexter functions throw an error,
// that error will be swallowed up and reported back to
// Sentry. Thus, this method is safe to insert as a probe into
// sensitive code that mustn't fail, regardless of the assertion, and
// regardless of the contexter.
//
export function check(description, predicate, contexter = EmptyContexter, notifier = sentryNotify) {
  try {
    assert(description, predicate, contexter, notifier);
  } catch (e) {
    if (e.name !== AssertionFailed) {
      // This error wasn't fabricated by us, so it hasn't already been reported.
      notifier({
        type: 'error',
        content: e,
        context: { errorWhileAsserting: description },
      });
    }
  }
}

//
// This is the same as `check`, except that it only reports back a
// failure once per page load. The check is uniqueified by the
// description.
//
const checksDone = [];
export function checkOnce(description, predicate, contexter = EmptyContexter) {
  check(description, predicate, contexter, (notification) => {
    // On notify, first check to see if we already have:
    if (checksDone.some((chk) => chk === description))
      return;

    // If we haven't, carry on:
    checksDone.push(description);
    sentryNotify(notification);
  })
}
